import { Component, OnInit } from '@angular/core';
import { ThirdPartiesService } from '../../services/third-parties.service';
import { LoadingService } from '../../services/loading.service';

@Component({
  selector: 'app-third-parties',
  templateUrl: './third-parties.component.html',
  styleUrl: './third-parties.component.scss'
})
export class ThirdPartiesComponent implements OnInit {

  public thirdParties: any[] = [];
  public searchTerm: string = '';
  public statusFilter: string = '';

  // Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;

  // Métricas
  totalThirdParties: number = 0;
  activeThirdParties: number = 0;
  inactiveThirdParties: number = 0;

  // Status options for dropdown
  public statusDropdownOptions: any[] = [
    { label: 'Todos', value: '' },
    { label: 'Activo', value: 'active' },
    { label: 'Inactivo', value: 'inactive' }
  ];

  private searchTimeout: any;

  constructor(
    private _thirdPartiesService: ThirdPartiesService,
    private _loadingService: LoadingService
  ) {

  }

  ngOnInit(): void {
    this.loadThirdParties();
  }

  loadThirdParties() {
    this._loadingService.show();
    this._thirdPartiesService.getPaged(this.pageNumber, this.pageSize, this.searchTerm, this.statusFilter).subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          this.thirdParties = response.data.data || [];
          this.totalRecords = response.data.totalCount || 0;
          this.calculateMetrics();
        } else {
          this.thirdParties = [];
          this.totalRecords = 0;
        }
        this._loadingService.hide();
      },
      error: (err) => {
        console.log("error", err);
        this.thirdParties = [];
        this.totalRecords = 0;
        this._loadingService.hide();
      }
    });
  }

  onFilterChange(): void {
    this.pageNumber = 1;
    this.loadThirdParties();
  }

  onPageChange(event: any): void {
    this.pageNumber = event.page + 1; // PrimeNG es 0-indexed
    this.pageSize = event.rows;
    this.loadThirdParties();
  }

  onSearchTermChange(): void {
    // Debounce search to avoid too many API calls
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.onFilterChange();
    }, 500);
  }

  onStatusFilterChange(): void {
    this.onFilterChange();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = '';
    this.onFilterChange();
  }

  getInitials(firstName: string, lastName: string, commercialName?: string): string {
    // Prioridad: nombre comercial o nombre/apellido
    if (commercialName) {
      const words = commercialName.trim().split(' ');
      return words.length >= 2 ? `${words[0][0]}${words[1][0]}`.toUpperCase() : `${words[0][0]}${words[0][1] || ''}`.toUpperCase();
    }
    const fi = firstName ? firstName[0] : '';
    const li = lastName ? lastName[0] : '';
    return `${fi}${li}`.toUpperCase() || '??';
  }

  getDisplayName(thirdParty: any): string {
    // Priorizar nombre comercial si existe
    if (thirdParty.commercialName) {
      return thirdParty.commercialName;
    }
    // Si hay nombre y apellido, mostrar ambos
    const firstName = thirdParty.firstName || '';
    const lastName = thirdParty.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Sin nombre';
  }

  getSecondaryInfo(thirdParty: any): string | null {
    // Si tiene nombre comercial Y nombre/apellido, mostrar el contacto como info secundaria
    if (thirdParty.commercialName && (thirdParty.firstName || thirdParty.lastName)) {
      const firstName = thirdParty.firstName || '';
      const lastName = thirdParty.lastName || '';
      return `Contacto: ${firstName} ${lastName}`.trim();
    }
    return null;
  }

  /** Tono cromático determinístico para el avatar de iniciales (a-f). */
  getInitialsTone(seed: string | null | undefined): string {
    const s = (seed || '?').toUpperCase();
    const code = s.charCodeAt(0) || 0;
    const tones = ['a', 'b', 'c', 'd', 'e', 'f'];
    return tones[code % tones.length];
  }

  getRelativeTime(date: Date | string | undefined): string {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInMs = now.getTime() - dateObj.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInDays > 0) {
      return `hace ${diffInDays} día${diffInDays > 1 ? 's' : ''}`;
    } else if (diffInHours > 0) {
      return `hace ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    } else if (diffInMinutes > 0) {
      return `hace ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
    } else {
      return 'hace un momento';
    }
  }

  private calculateMetrics(): void {
    this.totalThirdParties = this.totalRecords;
    this.activeThirdParties = this.thirdParties.filter(p => !p.isDeleted).length;
    this.inactiveThirdParties = this.thirdParties.filter(p => p.isDeleted).length;
  }
}
